import { queryOptions, useQuery } from '@tanstack/react-query';
import { gql, type Variables } from 'graphql-request';

import { useCartContext } from '@/app/contexts/CartProvider';
import useCurrency from '@/app/hooks/useCurrency';
import { getCartIdCookie } from '@/utils/cartUtils';
import gqlRequest from '@/utils/gqlRequest';

import type {
  LowestRingsPricing,
  LowestRingsPricing_content_lowestRingsPricing,
  LowestRingsPricingVariables,
} from './types/LowestRingsPricing';

export const query = gql`
  query LowestRingsPricing(
    $cartId: String
    $currency: String
    $flags: [String]
    $includeDiscount: Boolean
    $region: String
    $ringStyle: String
  ) {
    content(region: $region, currency: $currency, enabledFeatureFlags: $flags) {
      lowestRingsPricing(
        cartId: $cartId
        includeDiscount: $includeDiscount
        ringStyle: $ringStyle
      ) {
        price {
          amount
          currencyCode
        }
      }
    }
  }
`;

const DEFAULT_VARS: LowestRingsPricingVariables = {
  cartId: null,
  currency: null,
  flags: [],
  includeDiscount: false,
  region: null,
  ringStyle: null,
};

export const lowestRingsPricingOptions = (
  vars: LowestRingsPricingVariables,
  enabled: boolean,
) => {
  const _vars: LowestRingsPricingVariables = {
    ...DEFAULT_VARS,
    ...vars,
  };

  // Automatically include cartId if includeDiscount is true
  if (_vars.includeDiscount && !_vars.cartId) {
    _vars.cartId = getCartIdCookie();
  }

  return queryOptions({
    queryKey: ['LowestRingsPricing', { _vars }],
    queryFn:
      async (): Promise<LowestRingsPricing_content_lowestRingsPricing> => {
        const data = await gqlRequest<LowestRingsPricing>(
          query,
          _vars as unknown as Variables,
        );
        return data.content?.lowestRingsPricing ?? { price: null };
      },
    enabled,
  });
};

export function useLowestRingsPricing(
  vars: Omit<LowestRingsPricingVariables, 'currency'> = {},
) {
  const currency = useCurrency();
  const { refreshCartSuccess } = useCartContext();

  return useQuery(
    lowestRingsPricingOptions(
      {
        ...vars,
        currency: currency.currencyCode,
      },
      refreshCartSuccess,
    ),
  );
}
